import { config } from '@ps/config/client';
import type { BasicUserProfileInfo, CommentMessage, CommentResponse } from './types';

const base = () => config.awsPopclipServiceBaseUrl;

export async function addReplyAPI(
  commentId: string,
  clipId: string,
  commentTimestamp: number,
  text: string,
  userId: string,
  userMeta: BasicUserProfileInfo | null,
): Promise<CommentMessage> {
  const response = await fetch(`${base()}/api/popclips/comments/reply`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      comment_id: commentId,
      timestamp: commentTimestamp,
      text_content: text,
      clip_id: clipId,
      user_id: userId,
      user_meta: userMeta,
    }),
  });

  if (!response.ok) {
    throw new Error('Failed to add reply');
  }

  const json = (await response.json()) as { data: CommentResponse };
  const data = json.data;

  return {
    type: 'reply',
    commentId: data.parent_comment_id ?? commentId,
    id: data.comment_id,
    username: data.user_meta?.username || '',
    userId: data.user_id,
    text: data.comment_text,
    avatar: data.user_meta?.avatar || '',
    replyCount: 0,
    status: 'added',
    name: data.user_meta?.fullName || 'Unknown User',
    createdAt: data.created_at,
    updatedAt: data.updated_at,
    timestamp: data.timestamp,
  };
}

export async function fetchReplies(
  commentId: string,
  clipId: string,
  pageSize = 20,
  lastEvaluatedKey: {
    clip_id: string;
    comment_id: string;
    customer_id: string;
  } | null,
): Promise<{
  replies: CommentMessage[];
  lastEvaluatedKey: {
    clip_id: string;
    comment_id: string;
    customer_id: string;
  } | null;
}> {
  const response = await fetch(`${base()}/api/popclips/comments/fetchReplies`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      comment_id: commentId,
      clip_id: clipId,
      pageSize,
      lastEvaluatedKey,
    }),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch replies');
  }

  const json = (await response.json()) as {
    data: {
      comments: CommentResponse[];
      lastEvaluatedKey: {
        clip_id: string;
        comment_id: string;
        customer_id: string;
      } | null;
    };
  };

  const commentResponse = json.data.comments || [];

  const formattedReplies: CommentMessage[] = commentResponse
    .filter((reply) => !reply.is_deleted)
    .map((reply) => ({
      type: 'reply' as const,
      commentId: reply.parent_comment_id ?? commentId,
      id: reply.comment_id,
      username: reply.user_meta?.username || '',
      userId: reply.user_id,
      text: reply.comment_text,
      isDeleted: reply.is_deleted,
      replyCount: 0,
      avatar: reply.user_meta?.avatar || '',
      status: 'added' as const,
      name: reply.user_meta?.fullName || 'Unknown User',
      createdAt: reply.created_at,
      updatedAt: reply.updated_at,
      timestamp: reply.timestamp,
    }));

  return {
    replies: formattedReplies,
    lastEvaluatedKey: json.data.lastEvaluatedKey,
  };
}
